import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Calendar, Clock, Plus, Trash2, CheckCircle, XCircle, User, FileText, QrCode, Download, Users, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ResultOverlay } from '../../components/ResultOverlay';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const statusColors = { pending: 'bg-yellow-500/15 text-yellow-400', confirmed: 'bg-green-500/15 text-green-400', cancelled: 'bg-red-500/15 text-red-400', completed: 'bg-blue-500/15 text-blue-400' };
const statusLabels = { pending: 'En attente', confirmed: 'Confirmé', cancelled: 'Annulé', completed: 'Terminé' };

export default function AdminRdv() {
  const [tab, setTab] = useState('rdv');
  const [slots, setSlots] = useState([]);
  const [rdvs, setRdvs] = useState([]);
  const [rdvRequests, setRdvRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotOpen, setSlotOpen] = useState(false);
  const [slotForm, setSlotForm] = useState({ date: '', time: '', duration: '60', service: '' });
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteRdv, setNoteRdv] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [overlay, setOverlay] = useState({ show: false, type: 'success', title: '', subtitle: '' });

  const load = () => Promise.all([api.get('/admin/rdv/slots'), api.get('/admin/rdv'), api.get('/admin/reservations')])
    .then(([s, r, res]) => {
      setSlots(s.data);
      setRdvs(r.data);
      setRdvRequests((res.data || []).filter(x => x.type === 'rdv'));
    }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const createSlot = async () => {
    try {
      await api.post('/admin/rdv/slots', { ...slotForm, duration: parseInt(slotForm.duration) });
      toast.success('Créneau créé');
      setSlotOpen(false); load();
    } catch { toast.error('Erreur'); }
  };

  const deleteSlot = async (sid) => {
    try { await api.delete(`/admin/rdv/slots/${sid}`); toast.success('Supprimé'); load(); }
    catch { toast.error('Erreur'); }
  };

  const updateRdv = async (rid, status, notes) => {
    try {
      await api.put(`/admin/rdv/${rid}`, { status, notes_admin: notes || '' });
      if (status === 'confirmed') {
        setOverlay({ show: true, type: 'success', title: 'RDV confirmé !', subtitle: 'Le client a reçu un email de confirmation' });
      } else if (status === 'cancelled') {
        setOverlay({ show: true, type: 'error', title: 'RDV annulé', subtitle: "Le client a été notifié par email" });
      } else if (status === 'completed') {
        setOverlay({ show: true, type: 'party', title: 'RDV terminé !', subtitle: 'Super, bien joué !' });
      }
      load();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="admin-rdv-page">
      <ResultOverlay {...overlay} onClose={() => setOverlay(p => ({ ...p, show: false }))} />
      <h1 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>Gestion des RDV</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#121212] border border-white/5 h-auto p-1 mb-6">
          <TabsTrigger value="rdv" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-3 sm:px-4 gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> <span className="hidden sm:inline">RDV</span> ({rdvs.filter(r => r.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="requests" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-3 sm:px-4 gap-1.5">
            <Send className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Demandes</span> ({rdvRequests.filter(r => r.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="slots" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-3 sm:px-4 gap-1.5">
            <Clock className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{"Créneaux"}</span> ({slots.filter(s => s.available).length})
          </TabsTrigger>
        </TabsList>

        {/* RDV list */}
        <TabsContent value="rdv">
          {rdvs.length === 0 ? <p className="text-sm text-[#A1A1AA] text-center py-12">Aucun RDV</p> : (
            <div className="space-y-3">
              {rdvs.map(r => (
                <div key={r.rdv_id} className="bg-[#121212] border border-white/5 rounded-xl p-4" data-testid={`rdv-${r.rdv_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-sm font-semibold">{r.user?.name || r.user_name || 'Client'}</span>
                        <span className="text-[10px] text-[#A1A1AA]">{r.user?.email || r.user_email}</span>
                      </div>
                      <div className="flex gap-3 text-xs text-[#A1A1AA]">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{r.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.time}</span>
                        <span>{r.duration} min</span>
                        {r.service && <span className="text-[#D4AF37]">{r.service}</span>}
                      </div>
                    </div>
                    <Badge className={statusColors[r.status] || 'bg-white/10'}>{statusLabels[r.status] || r.status}</Badge>
                  </div>
                  {r.notes_admin && <p className="text-xs text-[#A1A1AA] bg-white/5 rounded-lg p-2 mb-2"><FileText className="w-3 h-3 inline mr-1" />Note: {r.notes_admin}</p>}
                  {r.status === 'pending' && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => updateRdv(r.rdv_id, 'confirmed')}
                        className="bg-[#10B981] text-white hover:bg-[#10B981]/80 rounded-full text-xs h-7" data-testid={`confirm-rdv-${r.rdv_id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Confirmer
                      </Button>
                      <Button size="sm" onClick={() => updateRdv(r.rdv_id, 'cancelled')}
                        className="bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full text-xs h-7" data-testid={`cancel-rdv-${r.rdv_id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Annuler
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setNoteRdv(r); setNoteText(r.notes_admin || ''); setNoteOpen(true); }}
                        className="text-[#A1A1AA] text-xs h-7"><FileText className="w-3 h-3 mr-1" /> Note</Button>
                      <a href={`${API_URL}/api/rdv/${r.rdv_id}/ics`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost" className="text-[#A1A1AA] text-xs h-7"><Download className="w-3 h-3 mr-1" /> .ics</Button>
                      </a>
                    </div>
                  )}
                  {r.status === 'confirmed' && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => updateRdv(r.rdv_id, 'completed')}
                        className="bg-blue-500/20 text-blue-400 rounded-full text-xs h-7">Marquer terminé</Button>
                      <a href={`${API_URL}/api/rdv/${r.rdv_id}/ics`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost" className="text-[#A1A1AA] text-xs h-7"><Download className="w-3 h-3 mr-1" /> .ics</Button>
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>


        {/* Demandes RDV (from reservations type=rdv) */}
        <TabsContent value="requests">
          {rdvRequests.length === 0 ? <p className="text-sm text-[#A1A1AA] text-center py-12">Aucune demande de RDV</p> : (
            <div className="space-y-3">
              {rdvRequests.map(r => (
                <div key={r.reservation_id} className="bg-[#121212] border border-white/5 rounded-xl p-4" data-testid={`rdv-request-${r.reservation_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-sm font-semibold">{r.user_name || 'Client'}</span>
                        <span className="text-[10px] text-[#A1A1AA]">{r.user_email}</span>
                      </div>
                      {r.prestation_type && <span className="text-xs text-[#D4AF37]">{r.prestation_type}</span>}
                      <div className="flex gap-3 text-xs text-[#A1A1AA] mt-1">
                        {r.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{r.date}</span>}
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    <Badge className={statusColors[r.status] || 'bg-white/10'}>{statusLabels[r.status] || r.status}</Badge>
                  </div>
                  {r.message && <p className="text-xs text-[#A1A1AA] bg-white/5 rounded-lg p-2 mb-2">{r.message}</p>}
                  {r.status === 'pending' && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={async () => {
                        try { await api.put(`/admin/reservations/${r.reservation_id}`, { status: 'confirmed' });
                          setOverlay({ show: true, type: 'success', title: 'Demande confirmée !', subtitle: 'Le client a reçu un email' }); load();
                        } catch { toast.error('Erreur'); }
                      }} className="bg-[#10B981] text-white hover:bg-[#10B981]/80 rounded-full text-xs h-7">
                        <CheckCircle className="w-3 h-3 mr-1" /> Confirmer
                      </Button>
                      <Button size="sm" onClick={async () => {
                        try { await api.put(`/admin/reservations/${r.reservation_id}`, { status: 'cancelled' });
                          setOverlay({ show: true, type: 'error', title: 'Demande refusée', subtitle: 'Le client a été notifié' }); load();
                        } catch { toast.error('Erreur'); }
                      }} className="bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full text-xs h-7">
                        <XCircle className="w-3 h-3 mr-1" /> Refuser
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Slots */}
        <TabsContent value="slots">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setSlotForm({ date: '', time: '', duration: '60', service: '' }); setSlotOpen(true); }}
              className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-4" data-testid="create-slot-btn">
              <Plus className="w-4 h-4 mr-1" /> {"Nouveau créneau"}
            </Button>
          </div>
          {slots.length === 0 ? <p className="text-sm text-[#A1A1AA] text-center py-12">{"Aucun créneau créé"}</p> : (
            <div className="space-y-2">
              {slots.map(s => (
                <div key={s.slot_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`slot-${s.slot_id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[#D4AF37]" />{s.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#D4AF37]" />{s.time}</span>
                      <span className="text-[#A1A1AA]">{s.duration} min</span>
                      {s.service && <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] text-[10px]">{s.service}</Badge>}
                    </div>
                  </div>
                  <Badge className={s.available ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}>
                    {s.available ? 'Disponible' : 'Réservé'}
                  </Badge>
                  <Button size="icon" variant="ghost" onClick={() => deleteSlot(s.slot_id)}
                    className="h-8 w-8 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Slot Dialog */}
      <Dialog open={slotOpen} onOpenChange={setSlotOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>{"Nouveau créneau"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-xs text-[#A1A1AA]">Date *</Label>
              <Input type="date" value={slotForm.date} onChange={e => setSlotForm(p => ({ ...p, date: e.target.value }))}
                data-testid="slot-date" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Heure *</Label>
              <Input type="time" value={slotForm.time} onChange={e => setSlotForm(p => ({ ...p, time: e.target.value }))}
                data-testid="slot-time" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#A1A1AA]">{"Durée (min)"}</Label>
                <Input type="number" value={slotForm.duration} onChange={e => setSlotForm(p => ({ ...p, duration: e.target.value }))}
                  data-testid="slot-duration" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
              <div><Label className="text-xs text-[#A1A1AA]">Service</Label>
                <Select value={slotForm.service} onValueChange={v => setSlotForm(p => ({ ...p, service: v }))}>
                  <SelectTrigger className="bg-[#121212] border-white/10 rounded-xl mt-1" data-testid="slot-service"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="DJ & Animation">DJ & Animation</SelectItem>
                    <SelectItem value="Bar à Mocktails">{"Bar à Mocktails"}</SelectItem>
                    <SelectItem value="Serveur">Serveur</SelectItem>
                    <SelectItem value="Complet">Complet</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={createSlot} data-testid="save-slot" className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">{"Créer le créneau"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Note interne</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={3}
              placeholder="Note visible uniquement par l'admin..." className="bg-[#121212] border-white/10 rounded-xl resize-none" />
            <Button onClick={async () => { if (noteRdv) { await updateRdv(noteRdv.rdv_id, noteRdv.status, noteText); setNoteOpen(false); } }}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">Sauvegarder</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
